import React, { useState, useEffect } from "react";
import { getRequest, postRequest, patchRequest } from "../../axiosRoutes/index";
import {
  Button,
  Modal,
  Box,
  Typography,
  OutlinedInput,
  FormControl,
  InputLabel,
  Grid,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useSnackbar } from "../../axiosRoutes/snackbarConfig";
import dayjs from "dayjs";

const currentYear = dayjs().year();
const minDate = `${currentYear}-01-01`;
const maxDate = `${currentYear+2}-12-31`;

const AddMedical = ({ open, handleClose, medicalId }) => {
  const { snackbar, showSnackbar, handleSnackbarClose } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [medicalData, setMedicalData] = useState({
    userId: "",
    date: "",
    reason: "",
    status: "",
    comments: "",
    amount: "",
    image: "",
  });

  const fetchStatus = async () => {
    try {
      const response = await getRequest("filter/status");
      const statusArray = Object.keys(response).map((key) => ({
        label: key,
        value: response[key],
      }));
      setStatuses(statusArray);
    } catch (error) {
      console.error("Error fetching Filter:", error);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    const fetchMedical = async () => {
      if (medicalId !== null) {
        const access = localStorage.getItem("access");
        try {
          setLoading(true);
          const response = await getRequest(
            `admin/medicalAllowance/${medicalId}`,
            {
              headers: {
                Authorization: `Bearer ${access}`,
              },
            }
          );
          const formatDate = new Date(response.date)
            .toISOString()
            .split("T")[0];
          setMedicalData({
            userId: response.userId,
            date: formatDate,
            reason: response.reason,
            status: response.status,
            comments: response.comments,
            amount: response.amount,
            image: response.imageUrl || "",
          });
        } catch (error) {
          console.error("Error fetching Medical Allowances:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setMedicalData({
          userId: "",
          date: "",
          reason: "",
          status: "",
          comments: "",
          amount: "",
          image: "",
        });
      }
    };
    fetchMedical();
  }, [medicalId]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      userId: medicalData.userId,
      date: medicalData.date,
      reason: medicalData.reason,
      status: medicalData.status,
      comments: medicalData.comments,
      amount: medicalData.amount,
    };
    if (medicalData.image) {
      if (typeof medicalData.image === "string") {
        payload.image = "";
      } else {
        const file = medicalData.image;
        const base64Image = await toBase64(file);
        payload.image = base64Image;
      }
    }

    try {
      const access = localStorage.getItem("access");
      await patchRequest(
        `admin/medicalAllowance/${medicalId}`,
        {
          Authorization: `Bearer ${access}`,
          "Content-Type": "application/json",
        },
        JSON.stringify(payload)
      );
      showSnackbar("Medical Updated Successfully", "success");
      handleClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      const access = localStorage.getItem("access");
      try {
        const response = await getRequest("employee/getAllEmployees", {
          headers: {
            Authorization: `Bearer ${access}`,
          },
        });
        const activeEmployees = response.filter(emp => emp.status === "active");
        setEmployees(activeEmployees);
      } catch (error) {
        console.error("Error fetching Employees:", error);
      }
    };
    fetchEmployees();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMedicalData({ ...medicalData, [name]: value });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMedicalData({ ...medicalData, image: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      userId: medicalData.userId,
      date: medicalData.date,
      reason: medicalData.reason,
      status: medicalData.status,
      comments: medicalData.comments,
      amount: medicalData.amount,
    };
    if (medicalData.image) {
      const file = medicalData.image;
      const base64Image = await toBase64(file);
      payload.image = base64Image;
    }

    try {
      const access = localStorage.getItem("access");
      await postRequest(
        "admin/medicalAllowance",
        {
          Authorization: `Bearer ${access}`,
          "Content-Type": "application/json",
        },
        JSON.stringify(payload)
      );
      showSnackbar("Medical Added Successfully", "success");
      setMedicalData({
        userId: "",
        date: "",
        reason: "",
        status: "",
        comments: "",
        amount: "",
      });
      handleClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  return (
    <>
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: '80%', md: '50%' },
            maxHeight: "80vh",
            overflowY: "auto",
            bgcolor: "black",
            color: "#FEFEFE",
            border: "1px solid #A9A9A9",
            p: 2,
            "& .MuiOutlinedInput-root": {
              color: "#FEFEFE",
              "& fieldset": {
                borderColor: "#FEFEFE",
              },
              "&:hover fieldset": {
                borderColor: "#FEFEFE",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FEFEFE",
              },
            },
            "& .MuiInputLabel-root": {
              color: "#FEFEFE",
            },
            "& .MuiIconButton-root": {
              color: "#FEFEFE",
            },
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h4"
            fontWeight="bold"
            textAlign="center"
            component="h1"
            sx={{ mb: 2 }}
          >
            {medicalId ? "Update Medical Allowance" : "Add Medical Allowance"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel id="employee-select-label">Employee</InputLabel>
                <Select
                  labelId="employee-select-label"
                  name="userId"
                  readOnly={medicalId ? true : false}
                  value={medicalData.userId}
                  onChange={handleChange}
                  label="Employee"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        overflowY: "auto",
                      },
                    },
                  }}
                >
                  {employees ? (employees.map((employee) => (
                    <MenuItem key={employee.userId._id} value={employee.userId._id}>
                      {employee.userId.name}
                    </MenuItem>
                  ))) : <MenuItem disabled={true}>No Data yet</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel htmlFor="outlined-adornment-Reason">
                  Reason
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-reason"
                  type="text"
                  name="reason"
                  label="Reason"
                  value={medicalData.reason}
                  onChange={handleChange}
                  sx={{ backgroundColor: "transparent" }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel htmlFor="outlined-adornment-Comments">
                  Comments
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-comments"
                  type="text"
                  name="comments"
                  label="Comments"
                  value={medicalData.comments}
                  onChange={handleChange}
                  sx={{ backgroundColor: "transparent" }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel htmlFor="outlined-adornment-Amount">
                  Amount
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-amount"
                  type="text"
                  name="amount"
                  label="Amount"
                  value={medicalData.amount}
                  onChange={handleChange}
                  sx={{ backgroundColor: "transparent" }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel
                  htmlFor="outlined-adornment-date"
                  shrink
                  sx={{
                    backgroundColor: "#000",
                    paddingX: "4px",
                    marginLeft: "-4px",
                  }}
                >
                  Date
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-date"
                  type="date"
                  name="date"
                  label="Date"
                  value={medicalData.date}
                  onChange={handleChange}
                  inputProps={{ min: minDate, max: maxDate }}
                  sx={{ backgroundColor: "transparent" }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                <InputLabel htmlFor="outlined-adornment-status">
                  Status
                </InputLabel>
                <Select
                  labelId="outlined-adornment-status-label"
                  id="outlined-adornment-status"
                  name="status"
                  label="Status"
                  value={medicalData.status}
                  onChange={handleChange}
                  sx={{ backgroundColor: "transparent" }}
                >
                  {statuses.map((statusOpt) => (
                    <MenuItem key={statusOpt.value} value={statusOpt.value}>
                      {statusOpt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: "none" }}
                />
                <Button
                  component="span"
                  size="small"
                  variant="contained"
                  sx={{ width: "auto", bgcolor: "#E5622D", "&:hover": { bgcolor: "#D0521B" }, }}
                >
                  {medicalData.image ? "Update Attachment" : "Add Attachment"}
                </Button>
              </label>
            </Grid>
            {medicalData.image && (
              <Grid item xs={12} sx={{ textAlign: "center" }}>
                {typeof medicalData.image === "string" ? (
                  <img
                    src={medicalData.image}
                    alt="Preview"
                    style={{ maxWidth: "25%", height: "auto" }}
                  />
                ) : (
                  <img
                    src={URL.createObjectURL(medicalData.image)}
                    alt="Preview"
                    style={{ maxWidth: "25%", height: "auto" }}
                  />
                )}
              </Grid>
            )}
            {medicalId !== null ? (
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleUpdate}
                  sx={{
                    bgcolor: "#E5622D",
                    "&:hover": { bgcolor: "#D0521B" },
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#FF5722" }} />
                  ) : (
                    "Update"
                  )}
                </Button>
              </Grid>
            ) : (
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit}
                  sx={{
                    bgcolor: "#E5622D",
                    "&:hover": { bgcolor: "#D0521B" },
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#FF5722" }} />
                  ) : (
                    "Add"
                  )}
                </Button>
              </Grid>
            )}
            <Grid item xs={6}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleClose}
                disabled={loading}
                sx={{ bgcolor: "#A9A9A9", "&:hover": { bgcolor: "#E1E1E1" } }}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddMedical;
